import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Send, CheckCircle } from 'lucide-react';
import { contatti as contattiMock } from '../data/mock';
import { toast } from 'sonner';
import { api, isBackendOnline } from '../api/client';
import SocialShare from '../components/SocialShare';
import { useSiteText } from '../hooks/useSiteText';

export default function Contatti() {
  const [form, setForm] = useState({ nome: '', email: '', messaggio: '' });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contatti, setContatti] = useState(contattiMock);
  const title = useSiteText('contatti.title', 'Contatti');
  const subtitle = useSiteText('contatti.subtitle', 'Hai domande sul torneo o vuoi iscrivere la tua squadra? Scrivici!');
  const infoTitle = useSiteText('contatti.info_title', 'Informazioni');
  const formTitle = useSiteText('contatti.form_title', 'Scrivici');
  const labelNome = useSiteText('contatti.form_nome', 'Nome completo');
  const labelEmail = useSiteText('contatti.form_email', 'Email');
  const labelMessaggio = useSiteText('contatti.form_messaggio', 'Messaggio');
  const submitBtn = useSiteText('contatti.form_submit', 'Invia messaggio');
  const followUs = useSiteText('contatti.follow_us', 'Seguici su');

  // Carica contatti dal backend (più completo di getSettings) con fallback su mock
  useEffect(() => {
    (async () => {
      try {
        const c = await api.getContatti();
        setContatti({
          email: c.email || contattiMock.email,
          phones: (c.phones && c.phones.length > 0) ? c.phones : contattiMock.phones,
          address: c.address || contattiMock.address,
          facebook: c.facebook_url || contattiMock.facebook,
          instagram: c.instagram_url || '',
          whatsapp: c.whatsapp || '',
          orari: c.orari || '',
          descrizione: c.descrizione || '',
          website: contattiMock.website,
          fax: contattiMock.fax,
        });
      } catch { /* offline o no backend: usa mock */ }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.messaggio) {
      toast.error('Compila tutti i campi');
      return;
    }
    setSubmitting(true);
    try {
      const online = await isBackendOnline();
      if (online) {
        await api.sendMessage(form);
      } else {
        // Fallback: salva in localStorage (modalità statica)
        const msgs = JSON.parse(localStorage.getItem('contactMessages') || '[]');
        msgs.push({ ...form, date: new Date().toISOString() });
        localStorage.setItem('contactMessages', JSON.stringify(msgs));
      }
      setSent(true);
      toast.success('Messaggio inviato! Ti risponderemo al più presto.');
      setForm({ nome: '', email: '', messaggio: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast.error(err.message || 'Errore invio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-navy">
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark to-navy"></div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <h1 className="font-brand text-5xl md:text-6xl font-bold text-white mb-4">{title}</h1>
          <div className="w-24 h-1 bg-pink-brand mx-auto rounded"></div>
          <p className="text-white/70 mt-6 max-w-xl mx-auto">
            {subtitle}
          </p>
          <div className="mt-6 flex justify-center">
            <SocialShare title="Contatti — Memorial Eugenio Abbro" />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            <h2 className="section-title text-3xl mb-8">{infoTitle}</h2>
            {[
              { icon: Mail, label: labelEmail, value: contatti.email, href: `mailto:${contatti.email}` },
              { icon: Phone, label: 'Telefono', value: contatti.phones.join(' / '), href: `tel:${contatti.phones[0]}` },
              { icon: MapPin, label: 'Sede', value: contatti.address, href: null },
            ].map((it, i) => {
              const Icon = it.icon;
              const content = (
                <div className="glass-card p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-brand flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-white" />
                  </div>
                  <div>
                    <div className="text-white/60 text-xs uppercase font-bold tracking-widest">{it.label}</div>
                    <div className="text-white font-semibold mt-1">{it.value}</div>
                  </div>
                </div>
              );
              return it.href ? <a key={i} href={it.href} className="block">{content}</a> : <div key={i}>{content}</div>;
            })}
            <a href={contatti.facebook} target="_blank" rel="noopener noreferrer" className="glass-card p-5 flex items-center gap-4 hover:border-pink-brand group" data-testid="contatti-facebook-card">
              <div className="w-12 h-12 rounded-xl bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                <Facebook size={22} className="text-white" />
              </div>
              <div>
                <div className="text-white/60 text-xs uppercase font-bold tracking-widest">{followUs}</div>
                <div className="text-white font-semibold mt-1 group-hover:text-pink-brand transition-colors">Facebook</div>
              </div>
            </a>
            {contatti.instagram && (
              <a href={contatti.instagram} target="_blank" rel="noopener noreferrer" className="glass-card p-5 flex items-center gap-4 hover:border-pink-brand group" data-testid="contatti-instagram-card">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#833ab4] flex items-center justify-center flex-shrink-0">
                  <Instagram size={22} className="text-white" />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase font-bold tracking-widest">{followUs}</div>
                  <div className="text-white font-semibold mt-1 group-hover:text-pink-brand transition-colors">Instagram</div>
                </div>
              </a>
            )}
          </div>

          <div>
            <h2 className="section-title text-3xl mb-8">{formTitle}</h2>
            <form onSubmit={handleSubmit} className="glass-card p-6 lg:p-8 space-y-5">
              <div>
                <label className="block text-white text-sm font-semibold mb-2">{labelNome}</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="w-full bg-navy-dark border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-pink-brand transition-colors" placeholder="Mario Rossi" />
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-2">{labelEmail}</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-navy-dark border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-pink-brand transition-colors" placeholder="mario.rossi@email.it" />
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-2">{labelMessaggio}</label>
                <textarea rows={5} value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })} className="w-full bg-navy-dark border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-pink-brand transition-colors resize-none" placeholder="Scrivi qui il tuo messaggio..." />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60">
                {sent ? <><CheckCircle size={18}/> Inviato!</> : submitting ? 'Invio in corso...' : <><Send size={18}/> {submitBtn}</>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
